import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { axiosPrivate } from 'app/utils';

import { plusWebsitesQuery } from '../../../hooks/usePlusWebsites';
import { multipartFormDataConfig } from '../../ThemeAndStyles/hooks/multipartFormDataConfig';

interface CreatePlusWebsiteResponse {
  id: string;
}

// The API answers as soon as the website is written, but the list endpoint reads a projection
// built after the response is sent. Onboarding hands the user over to the builder, which cannot
// render a website it cannot read and sends them back to the first onboarding step instead — so
// creation is only finished once the new website is in the list. A projection that has not landed
// within this budget is not going to; the journey carries on rather than trapping the user.
const READ_MODEL_ATTEMPTS = 8;
const READ_MODEL_INTERVAL_MS = 250;

function delay(ms: number): Promise<void> {
  const { promise, resolve } = Promise.withResolvers<void>();
  setTimeout(resolve, ms);
  return promise;
}

interface CreatePlusWebsiteParams {
  organisationId: string;
  primaryColor: string;
  logo?: File | null;
  logoUrl?: string;
  landingType: 'in-app' | 'external';
  externalHomepageUrl?: string;
  domain?: string;
}

function createPlusWebsite(params: CreatePlusWebsiteParams) {
  const formData = new FormData();
  formData.append('organisationId', params.organisationId);
  formData.append('primaryColor', params.primaryColor);
  formData.append('landingType', params.landingType);

  if (params.logoUrl) {
    formData.append('logoUrl', params.logoUrl);
  } else if (params.logo) {
    formData.append('logo', params.logo);
  }

  if (params.landingType === 'external' && params.externalHomepageUrl) {
    formData.append('externalHomepageUrl', params.externalHomepageUrl);
  }

  if (params.domain) {
    formData.append('domain', params.domain);
  }

  return axiosPrivate
    .post<CreatePlusWebsiteResponse>(
      '/v3/pharmacist/plus-websites',
      formData,
      multipartFormDataConfig
    )
    .then((r) => r.data);
}

async function waitUntilListed(
  queryClient: QueryClient,
  organisationId: string,
  websiteId: string
): Promise<void> {
  for (let attempt = 0; attempt < READ_MODEL_ATTEMPTS; attempt += 1) {
    if (attempt > 0) await delay(READ_MODEL_INTERVAL_MS);

    // Fetched rather than invalidated so the answer is in hand: the list decides where the user
    // goes next, and every observer of it is refreshed on the way. A list that will not load is
    // treated as one that has not caught up yet — the website is created either way, and failing
    // the journey over the list would cost the user the success page they earned.
    const response = await queryClient
      .fetchQuery({ ...plusWebsitesQuery(organisationId), staleTime: 0 })
      .catch(() => undefined);

    if (response?.data.plusWebsites.some((website) => website.id === websiteId)) return;
  }
}

export function useCreatePlusWebsite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createPlusWebsite,
    onSuccess: (data, variables) => waitUntilListed(queryClient, variables.organisationId, data.id),
    meta: {
      successMessage: 'Website created successfully',
      errorMessage: 'Failed to create website',
    },
  });
}
